import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { sendMetaAndShareMeeting } from '@src/bridge/bridgeMessenger';
import { useRoomContext } from '@src/contexts';
import { useRecording } from '@src/hooks';
import SocketService from '@src/services/SocketService';
import { useCalculatorStore, useMagicPenStore, useRoomActionsStore, useRoomStore } from '@src/stores';
import { ECommonSocketKeys, EUserType } from '@src/types';
import { useShallow } from 'zustand/react/shallow';
import { EBannerType } from './RoomHeader.type';

const ORDERED_BANNERS = [EBannerType.SHARING_ANNOTATING, EBannerType.CALCULATOR, EBannerType.RECORDING];

const useRoomHeader = () => {
  const shareButtonRef = useRef<HTMLDivElement>(null);

  const {
    shareRoomSettings: { onClick: onShareRoomClick },
    shareScreenSettings: { onClick: onShareScreenClick },
    screenRecordingSettings: { isActive: isScreenRecordActive },
  } = useRoomActionsStore(
    useShallow((state) => ({
      shareRoomSettings: state.shareRoomSettings,
      screenRecordingSettings: state.screenRecordingSettings,
      shareScreenSettings: state.shareScreenSettings,
    }))
  );

  const isMobile = useMediaQuery('(max-width:768px)');
  const { roomId, roomTitle, isRoomSettingsOpen, setIsRoomSettingsOpen, meetingType } = useRoomStore(
    useShallow((state) => ({
      roomId: state.roomId,
      roomTitle: state.roomTitle,
      isRoomSettingsOpen: state.isRoomSettingsOpen,
      setIsRoomSettingsOpen: state.setIsRoomSettingsOpen,
      meetingType: state.meetingType,
    }))
  );

  const { annotatorIds } = useMagicPenStore(
    useShallow((state) => ({
      annotatorIds: state.annotatorIds,
    }))
  );

  const { havePermissionToStopRecording, handleRecordAction } = useRecording();

  const [expandedAccordion, setExpandedAccordion] = useState<EBannerType | null>(null);
  const prevActiveBannersRef = useRef({
    sharingAnnotating: false,
    calculator: false,
    recording: false,
  });

  const { shareScreenStream, isHost, isHostOrCohost, participantsPeers } = useRoomContext();
  const screenSharerName = shareScreenStream?.peerName;
  const isCurrentUserSharing = shareScreenStream?.isCurrentUser || false;
  const { sharerIsHost, isAnnotating, annotatorCount } = useMemo(() => {
    const sharerPeer = participantsPeers.find((p) => p.id === shareScreenStream?.peerId);
    const othersCount = annotatorIds.filter((id) => id !== shareScreenStream?.peerId).length;
    return {
      sharerIsHost: sharerPeer?.peer_info?.user_type === EUserType.HOST,
      isAnnotating: annotatorIds.length > 0,
      annotatorCount: othersCount,
    };
  }, [participantsPeers, shareScreenStream?.peerId, annotatorIds]);

  const handleStopPresenting = async () => {
    if (isCurrentUserSharing) {
      onShareScreenClick(false);
    } else if (shareScreenStream?.peerId) {
      try {
        await SocketService.emit(ECommonSocketKeys.FORCE_STOP_SCREEN_SHARING, {
          targetPeerId: shareScreenStream.peerId,
        });
      } catch (error) {
        console.error('forceStopScreenSharing failed:', error);
      }
    }
  };

  const havePermissionToStopPresenting =
    !!screenSharerName && (isCurrentUserSharing || isHost || (isHostOrCohost && !sharerIsHost));

  const { sharedCalculatorData } = useCalculatorStore(
    useShallow((state) => ({
      sharedCalculatorData: state.sharedCalculatorData,
    }))
  );

  const sharerName = sharedCalculatorData.owner?.peer_name || sharedCalculatorData.owner?.peer_id || '';

  const canStopCalculatorSharing = () => {
    if (sharedCalculatorData.isSharedByMe) return true;

    const { owner } = sharedCalculatorData;
    if (!owner) return false;

    const ownerPeer = participantsPeers.find(
      (peer) => (peer.peer_uuid && peer.peer_uuid === owner.peer_uuid) || peer.id === owner.peer_id
    );
    const ownerIsHost = ownerPeer?.peer_info?.user_type === 'HOST';

    return ownerIsHost ? isHost : isHostOrCohost;
  };

  const havePermissionToStopCalculator = sharedCalculatorData.isOpen && canStopCalculatorSharing();

  const meetingUrl = window.location.href;

  const handleShareRoomClick = () => {
    if (!sendMetaAndShareMeeting(meetingUrl)) {
      onShareRoomClick(true);
    }
  };

  const getHighestPriority = (activeBanners: Record<EBannerType, boolean>, exclude?: EBannerType) =>
    ORDERED_BANNERS.find((banner) => activeBanners[banner] && banner !== exclude) || null;

  const getActiveBanners = useCallback(
    () => ({
      [EBannerType.SHARING_ANNOTATING]: !!screenSharerName,
      [EBannerType.CALCULATOR]: sharedCalculatorData.isOpen,
      [EBannerType.RECORDING]: isScreenRecordActive,
    }),
    [screenSharerName, sharedCalculatorData.isOpen, isScreenRecordActive]
  );

  useEffect(() => {
    const activeBanners = getActiveBanners();
    const prev = prevActiveBannersRef.current;
    const highestPriority = getHighestPriority(activeBanners);

    const prevMap = {
      [EBannerType.SHARING_ANNOTATING]: prev.sharingAnnotating,
      [EBannerType.CALCULATOR]: prev.calculator,
      [EBannerType.RECORDING]: prev.recording,
    };

    const newlyActiveBanner = ORDERED_BANNERS.find((banner) => !prevMap[banner] && activeBanners[banner]) || null;

    if (expandedAccordion && !activeBanners[expandedAccordion]) {
      setExpandedAccordion(highestPriority);
    } else if (
      newlyActiveBanner &&
      (!expandedAccordion || ORDERED_BANNERS.indexOf(newlyActiveBanner) < ORDERED_BANNERS.indexOf(expandedAccordion))
    ) {
      setExpandedAccordion(newlyActiveBanner);
    } else if (highestPriority && !expandedAccordion) {
      setExpandedAccordion(highestPriority);
    } else if (!highestPriority) {
      setExpandedAccordion(null);
    }

    prevActiveBannersRef.current = {
      sharingAnnotating: activeBanners[EBannerType.SHARING_ANNOTATING],
      calculator: activeBanners[EBannerType.CALCULATOR],
      recording: activeBanners[EBannerType.RECORDING],
    };
  }, [expandedAccordion, getActiveBanners]);

  const handleToggleAccordion = (accordion: EBannerType) => {
    if (expandedAccordion === accordion) {
      const nextPriority = getHighestPriority(getActiveBanners(), accordion);
      if (nextPriority) {
        setExpandedAccordion(nextPriority);
      }
    } else {
      setExpandedAccordion(accordion);
    }
  };

  const activeBanners = getActiveBanners();
  const canClose = Object.values(activeBanners).filter(Boolean).length > 1;

  return {
    isMobile,
    roomTitle,
    roomId,
    isRoomSettingsOpen,
    setIsRoomSettingsOpen,
    meetingType,
    isScreenRecordActive,
    havePermissionToStopRecording,
    handleRecordAction,
    screenSharerName,
    sharerName,
    havePermissionToStopCalculator,
    handleShareRoomClick,
    shareButtonRef,
    meetingUrl,
    sharedCalculatorData,
    havePermissionToStopPresenting,
    handleStopPresenting,
    expandedAccordion,
    handleToggleAccordion,
    canClose,
    isAnnotating,
    annotatorCount,
    isCurrentUserSharing,
    isHost,
  };
};

export default useRoomHeader;
